import Navigation from "@/components/Navigation";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { fadeInUp } from "@/motion/variants";
import { Button } from "@/components/ui/button";
import { ArrowUp } from "lucide-react";
import { useEffect, useState } from "react";
import {
  PADMORA_EIN,
  PADMORA_FORMATION_STATE,
  PADMORA_LEGAL_NAME,
  PRIVACY_POLICY_LAST_UPDATED,
} from "@/lib/constants";

const Privacy = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Privacy Policy | Data Protection | GDPR Compliance | Padmora"
        description="How Padmora collects, uses, and protects your data under GDPR, CCPA, and other applicable laws."
        noindex={false}
        keywords={[
          "privacy policy",
          "data protection",
          "GDPR compliance",
          "data collection",
          "privacy rights",
          "data security",
          "user privacy",
          "data handling",
          "privacy protection",
          "CCPA compliance"
        ]}
        aiOptimization={{
          aiKeywords: [
            "automated privacy compliance",
            "AI data protection",
            "intelligent privacy management",
            "machine learning privacy screening"
          ],
          entities: [
            "Privacy Policy",
            "GDPR",
            "Data Protection",
            "United States",
            "User Rights",
            "Data Security"
          ],
          topics: [
            "Privacy Compliance",
            "Data Protection",
            "Legal Compliance",
            "User Privacy",
            "Data Security"
          ]
        }}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Privacy Policy",
          "description": "Comprehensive privacy policy detailing data collection, usage, and protection practices.",
          "mainEntity": {
            "@type": "CreativeWork",
            "name": "Padmora Privacy Policy",
            "description": "Official privacy policy and data protection guidelines",
            "about": [
              {
                "@type": "Thing",
                "name": "Data Protection",
                "description": "Personal data collection, processing, and protection"
              },
              {
                "@type": "Thing",
                "name": "GDPR Compliance",
                "description": "Compliance with General Data Protection Regulation"
              }
            ]
          },
          "publisher": {
            "@type": "Organization",
            "name": "Padmora",
            "url": "https://padmora.com"
          }
        }}
        breadcrumbs={[
          { name: "Home", url: "https://padmora.com/" },
          { name: "Privacy Policy", url: "https://padmora.com/privacy" }
        ]}
        canonical="https://padmora.com/privacy"
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="min-h-[60vh] flex items-center justify-center">
        {/* Content */}
        <div className="container mx-auto px-6 py-20">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-800 text-foreground mb-6 leading-tight">
              Privacy <span className="text-primary">Policy</span>
            </h1>
            <p className="text-xl text-secondary font-500 mb-8 leading-relaxed max-w-2xl mx-auto">
              How we protect and handle your data with transparency and care
            </p>
          </div>
        </div>
      </section>
      
      {/* Content Section */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <motion.div
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto"
          >
            <div className="prose prose-slate max-w-none">
              {/* Who We Are */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">1. Who We Are</h2>
                <p className="text-secondary leading-relaxed">
                  This policy applies to {PADMORA_LEGAL_NAME} ({PADMORA_FORMATION_STATE}, USA, EIN: {PADMORA_EIN}). We are committed to protecting your privacy
                  and ensuring compliance with applicable data protection laws including GDPR and CCPA.
                </p>
              </div>

              {/* What We Collect */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">2. What We Collect</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-600 text-foreground mb-2">Personal Information</h3>
                    <p className="text-secondary leading-relaxed">
                      When you fill out forms, sign up, or interact with us—name, email, contact details, billing information, 
                      and any other information you provide to us.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-600 text-foreground mb-2">Usage Data & Cookies</h3>
                    <p className="text-secondary leading-relaxed">
                      We use analytics and cookies to understand how you use Padmora and to improve services. This includes 
                      IP addresses, browser information, usage patterns, and performance data.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-600 text-foreground mb-2">Service Data</h3>
                    <p className="text-secondary leading-relaxed">
                      Data you create, upload, or store through our managed hosting services, including application data,
                      configuration files, and user-generated content.
                    </p>
                  </div>
                </div>
              </div>

              {/* How We Use Data */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">3. How We Use Data</h2>
                <ul className="list-disc pl-6 text-secondary space-y-2">
                  <li>Provide and improve our managed hosting services</li>
                  <li>Manage accounts, billing, and customer communication</li>
                  <li>Process B2B quotation requests, store orders, and related customer care</li>
                  <li>Analyze usage and trends for better user experience</li>
                  <li>Ensure service security and prevent fraud</li>
                  <li>Comply with legal obligations and enforce our terms</li>
                  <li>Provide technical support and troubleshooting</li>
                  <li>Send service updates and important notifications</li>
                </ul>
              </div>

              {/* Who Has Access */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">4. Who Has Access</h2>
                <div className="space-y-4">
                  <p className="text-secondary leading-relaxed">
                    <strong className="text-foreground">Internal Access:</strong> Your data is accessible by authorized {PADMORA_LEGAL_NAME} personnel,
                    strictly for service delivery and internal analytics. Access is limited to authorized personnel who need 
                    it to provide our services.
                  </p>
                  <p className="text-secondary leading-relaxed">
                    <strong className="text-foreground">No Third-Party Sales:</strong> We never sell, rent, or trade your data to third parties.
                  </p>
                  <p className="text-secondary leading-relaxed">
                    <strong className="text-foreground">Service Providers:</strong> We may use third-party tools for analytics, payments, or email,
                    but only with GDPR-compliant partners who are bound by strict data protection agreements.
                  </p>
                  <p className="text-secondary leading-relaxed">
                    <strong className="text-foreground">Legal Requirements:</strong> We may disclose data if required by law, court order, or to protect 
                    our rights, property, or safety.
                  </p>
                </div>
              </div>

              {/* Data Processing & Storage */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">5. Data Processing & Storage</h2>
                <div className="space-y-4">
                  <p className="text-secondary leading-relaxed">
                    <strong className="text-foreground">Processing Purpose:</strong> We process your data to provide our trading platform, B2B store,
                    managed hosting services, and related support. We do not use your personal data for unrelated commercial purposes.
                  </p>
                  <p className="text-secondary leading-relaxed">
                    <strong className="text-foreground">Data Controller vs Processor:</strong> For your application data and content on our hosting, you remain
                    the data controller; we act as a data processor for that hosting relationship. For personal data you provide through our website,
                    store, or quotation flows, we act as a <strong className="text-foreground">data controller</strong> as described in this policy.
                  </p>
                  <p className="text-secondary leading-relaxed">
                    <strong className="text-foreground">Infrastructure Security:</strong> Your data is stored on secure, compliant cloud infrastructure 
                    managed by trusted third-party providers, with encryption at rest and in transit.
                  </p>
                </div>
              </div>

              {/* Cookies */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">6. Cookies</h2>
                <p className="text-secondary leading-relaxed mb-4">
                  We use cookies and similar technologies for analytics, personalization, and to remember your preferences. 
                  You can control cookies via your browser settings.
                </p>
                <div className="bg-muted/30 p-4 rounded-lg mb-4">
                  <h4 className="font-600 text-foreground mb-2">Types of Cookies We Use:</h4>
                  <ul className="list-disc pl-6 text-secondary space-y-1 text-sm">
                    <li><strong className="text-foreground">Essential:</strong> Required for basic site functionality</li>
                    <li><strong className="text-foreground">Analytics:</strong> Help us understand how visitors use our site</li>
                    <li><strong className="text-foreground">Preference:</strong> Remember your settings and preferences</li>
                    <li><strong className="text-foreground">Security:</strong> Help protect against fraud and abuse</li>
                  </ul>
                </div>
                
                {/* RB2B Tracking Disclosure */}
                <div className="bg-muted/30 p-4 rounded-lg mt-4">
                  <h4 className="font-600 text-foreground mb-2">Online Data Partners and Tracking</h4>
                  <p className="text-secondary leading-relaxed">
                    When you visit or log in to our website, cookies and similar technologies may be used by our online data partners or vendors to associate these activities with other personal information they or others have about you, including by association with your email. We (or service providers on our behalf) may then send communications and marketing to these email addresses. You may opt out of receiving this advertising by visiting{" "}
                    <a 
                      href="https://app.retention.com/optout" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      https://app.retention.com/optout
                    </a>. You also have the option to opt out of the collection of your personal data in compliance with GDPR. To exercise this option, please visit{" "}
                    <a 
                      href="https://www.rb2b.com/rb2b-gdpr-opt-out" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      https://www.rb2b.com/rb2b-gdpr-opt-out
                    </a>.
                  </p>
                </div>
              </div>

              {/* Data Retention */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">7. Data Retention</h2>
                <p className="text-secondary leading-relaxed mb-4">
                  We keep your data only as long as needed for the purposes above or as required by law:
                </p>
                <ul className="list-disc pl-6 text-secondary space-y-2">
                  <li><strong className="text-foreground">Account Data:</strong> Retained while your account is active and for a reasonable period after closure</li>
                  <li><strong className="text-foreground">Service Data:</strong> Retained for the duration of your service and as needed for backup/recovery</li>
                  <li><strong className="text-foreground">Analytics Data:</strong> Typically retained for up to 2 years</li>
                  <li><strong className="text-foreground">Billing Data:</strong> Retained as required by tax and accounting laws</li>
                </ul>
              </div>

              {/* Your Rights */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">8. Your Rights</h2>
                <p className="text-secondary leading-relaxed mb-4">
                  Depending on your location, you may have the following rights regarding your personal data:
                </p>
                <ul className="list-disc pl-6 text-secondary space-y-2">
                  <li><strong className="text-foreground">Access:</strong> Request a copy of your personal data</li>
                  <li><strong className="text-foreground">Correction:</strong> Update or correct inaccurate data</li>
                  <li><strong className="text-foreground">Deletion:</strong> Request deletion of your data (subject to legal requirements)</li>
                  <li><strong className="text-foreground">Portability:</strong> Request data export in a machine-readable format</li>
                  <li><strong className="text-foreground">Restriction:</strong> Limit how we process your data</li>
                  <li><strong className="text-foreground">Objection:</strong> Object to certain types of processing</li>
                  <li><strong className="text-foreground">Withdrawal:</strong> Withdraw consent where processing is based on consent</li>
                </ul>
                <p className="text-secondary leading-relaxed mt-4">
                  To exercise these rights, contact us at michel@padmora.com. We will respond within 30 days.
                </p>
              </div>

              {/* International Transfers */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">9. International Transfers</h2>
                <p className="text-secondary leading-relaxed">
                  Your info may be processed in the US or where our partners operate. We protect your data 
                  according to this policy and ensure appropriate safeguards are in place for international transfers, 
                  including standard contractual clauses where required.
                </p>
              </div>

              {/* Security */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">10. Security</h2>
                <p className="text-secondary leading-relaxed mb-4">
                  We use industry-standard security practices to protect your info, including:
                </p>
                <ul className="list-disc pl-6 text-secondary space-y-2">
                  <li>Encryption of data at rest and in transit</li>
                  <li>Regular security assessments and updates</li>
                  <li>Access controls and authentication</li>
                  <li>Secure infrastructure and monitoring</li>
                  <li>Employee training on data protection</li>
                </ul>
                <p className="text-secondary leading-relaxed mt-4">
                  However, no online service is 100% secure. We cannot guarantee absolute security but commit to 
                  maintaining high standards and promptly addressing any security concerns.
                </p>
              </div>

              {/* Data Breach Notification */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">11. Data Breach Notification</h2>
                <p className="text-secondary leading-relaxed">
                  In the unlikely event of a data breach that affects your personal data, we will notify you and 
                  relevant authorities as required by law. We will provide information about the nature of the breach, 
                  affected data, and steps we're taking to address it.
                </p>
              </div>

              {/* Children's Privacy */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">12. Children's Privacy</h2>
                <p className="text-secondary leading-relaxed">
                  Our services are not intended for children under 13. We do not knowingly collect personal information 
                  from children under 13. If we become aware that we have collected such information, we will take 
                  steps to delete it promptly.
                </p>
              </div>

              {/* Changes */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">13. Changes</h2>
                <p className="text-secondary leading-relaxed">
                  We may update this policy to reflect changes in our practices or legal requirements. We'll notify you 
                  of major changes via email or through our services. Continued use means acceptance of the updated policy.
                </p>
              </div>

              {/* Contact */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">14. Contact</h2>
                <p className="text-secondary leading-relaxed">
                  Questions or concerns about this Privacy Policy or our data practices? Email michel@padmora.com.
                </p>
                <p className="text-secondary leading-relaxed mt-2">
                  <strong className="text-foreground">Data Protection Officer:</strong> Michel Padrón (michel@padmora.com)
                </p>
              </div>

              {/* Last Updated */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <p className="text-sm text-secondary">
                  <strong className="text-foreground">Last Updated:</strong>{" "}
                  {new Date(PRIVACY_POLICY_LAST_UPDATED + "T12:00:00").toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              </div>
            </div>

            {/* Back to Top Button */}
            {showBackToTop && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="fixed bottom-8 right-8 z-50"
              >
                <Button
                  onClick={scrollToTop}
                  size="sm"
                  className="rounded-full shadow-lg hover:shadow-xl transition-shadow"
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Privacy;